import React, { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ReplayViewer.module.css';
import Board from '../Board';
import GlobalHeader from '../GlobalHeader';
import { AuthContext } from '../Providers/AuthContext';
import { GameContext } from '../Providers/GameContext';
import { GameRecordDocument } from '../../services/firebase/gameRecordService';
import { buildReplayGameState } from '../../services/zodiacEngine/Fen/FENDecoder';
import { inferMoveFromFENDelta } from '../../services/zodiacEngine/Fen/fenDeltaUtils';
import AIModelSelectionModal from '../ChallengeAI/AIModelSelectionModal';
import { AIModel, TimeFrame } from '../../services/zodiacEngine/AI/Core/AIVariants';
import { isArenaVariantId, resolveVariantDescriptor } from '../../services/AIArena/AIArenaConstants';
import { playUISound } from '../../utils/UISoundEffects';

interface ReplayViewerProps {
  gameRecord: GameRecordDocument;
  onClose: () => void;
}

export default function ReplayViewer({ gameRecord, onClose }: ReplayViewerProps) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { updateGameState } = useContext(GameContext);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showKings, setShowKings] = useState(false);

  // AI takeover state
  const [takeoverSide, setTakeoverSide] = useState<'light' | 'dark' | null>(null);
  const [showAISelector, setShowAISelector] = useState(false);
  const [selectedModel, setSelectedModel] = useState<AIModel | null>(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<TimeFrame>(TimeFrame.STANDARD);

  // FEN delta move description
  const [moveDescription, setMoveDescription] = useState<string>('');

  const fenHistory = gameRecord.fenHistory;
  const totalMoves = fenHistory.length;

  // Win condition label map
  const winConditionLabels: Record<string, string> = {
    'race': 'Race Victory',
    'kingslayer': 'Kingslayer',
    'zombie-race': 'Zombie Race Victory',
    'timeout': 'Time-out',
    'forfeit': 'Forfeit',
    'surrender': 'Surrender',
    'draw-stalemate': 'Stalemate',
    'draw-3-peat': 'Threefold Repetition',
  };

  // Navigate and compute delta (only shows when stepping forward)
  const navigateTo = useCallback((newIndex: number) => {
    setCurrentIndex((prev) => {
      const clamped = Math.max(0, Math.min(totalMoves - 1, newIndex));
      if (clamped > prev && clamped > 0 && clamped < totalMoves) {
        try {
          let desc = inferMoveFromFENDelta(fenHistory[clamped - 1], fenHistory[clamped]) || '';
          // On final move, append win condition
          if (clamped === totalMoves - 1 && gameRecord.winCondition && gameRecord.result !== 'draw') {
            const label = winConditionLabels[gameRecord.winCondition] || gameRecord.winCondition;
            // Extract piece name from delta (e.g. "Dark Snake" from "Dark Snake moved [C2 -> C1]")
            const pieceMatch = desc.match(/^((?:Light|Dark)\s+\w+)/);
            const subject = pieceMatch ? pieceMatch[1] : (gameRecord.result === 'light' ? 'Light' : 'Dark');
            desc = `${desc}\n${subject} won by ${label}`;
          } else if (clamped === totalMoves - 1 && gameRecord.result === 'draw') {
            const label = winConditionLabels[gameRecord.winCondition] || 'Draw';
            desc = `${desc}\nGame ended in ${label}`;
          }
          setMoveDescription(desc);
        } catch {
          setMoveDescription('');
        }
      } else {
        setMoveDescription('');
      }
      return clamped;
    });
  }, [totalMoves, fenHistory, gameRecord.winCondition, gameRecord.result]);

  // Build synthetic game state for current FEN
  const replayGameState = useMemo(() => {
    if (totalMoves === 0) return null;
    return buildReplayGameState(
      fenHistory[currentIndex],
      gameRecord.lightKing,
      gameRecord.darkKing,
      currentIndex,
      showKings
    );
  }, [currentIndex, showKings, fenHistory, gameRecord.lightKing, gameRecord.darkKing, totalMoves]);

  // Sync replay game state to GameContext so SquareRenderer sees Mode.Dev for crown rendering
  useEffect(() => {
    if (replayGameState) {
      updateGameState(replayGameState);
    }
  }, [replayGameState, updateGameState]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (showAISelector) return;
    switch (e.key) {
      case 'ArrowLeft':
        navigateTo(currentIndex - 1);
        e.preventDefault();
        break;
      case 'ArrowRight':
        navigateTo(currentIndex + 1);
        e.preventDefault();
        break;
      case 'Home':
        navigateTo(0);
        e.preventDefault();
        break;
      case 'End':
        navigateTo(totalMoves - 1);
        e.preventDefault();
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [totalMoves, showAISelector, onClose, navigateTo, currentIndex]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const isLight = gameRecord.lightPlayerId === user?.uid;

  const opponent = isLight
    ? gameRecord.darkPlayerUsername
    : gameRecord.lightPlayerUsername;

  // Takeover handlers
  function handleTakeoverClick(side: 'light' | 'dark') {
    setTakeoverSide(side);
    setSelectedModel(null);
    setShowAISelector(true);
  }

  function handleAIModelSelect(model: AIModel) {
    setSelectedModel(model);
    setShowAISelector(false); // Close model modal to reveal time frame selector
  }

  function handleConfirmTakeover() {
    if (!selectedModel || !takeoverSide) return;

    navigate('/challenge-ai', {
      state: {
        replayTakeover: {
          fen: fenHistory[currentIndex],
          lightKing: gameRecord.lightKing,
          darkKing: gameRecord.darkKing,
          playerSide: takeoverSide,
          aiModel: selectedModel,
          aiTimeFrame: selectedTimeFrame,
        },
      },
    });
  }

  function handleCancelTakeover() {
    setShowAISelector(false);
    setTakeoverSide(null);
    setSelectedModel(null);
  }

  function handleSandbox() {
    navigate('/sandbox', {
      state: {
        replayTakeover: {
          fen: fenHistory[currentIndex],
        },
      },
    });
  }

  const [copyLabel, setCopyLabel] = useState('Save');

  function handleCopyGame() {
    const lines: string[] = [];
    lines.push(`Game: ${gameRecord.lightPlayerUsername} (Light) vs ${gameRecord.darkPlayerUsername} (Dark)`);
    lines.push(`Result: ${gameRecord.result === 'draw' ? 'Draw' : gameRecord.result === 'light' ? 'Light wins' : 'Dark wins'}${gameRecord.winCondition ? ` (${gameRecord.winCondition})` : ''}`);
    lines.push(`Turns: ${gameRecord.turnCount} | Time Control: ${gameRecord.timeControlPreset}`);
    lines.push(`Light King: ${gameRecord.lightKing} | Dark King: ${gameRecord.darkKing}`);
    lines.push('');

    for (let i = 0; i < fenHistory.length; i++) {
      let delta = '';
      if (i > 0) {
        try {
          delta = inferMoveFromFENDelta(fenHistory[i - 1], fenHistory[i]) || '';
        } catch { /* ignore */ }
      }
      lines.push(`[Move ${i}]${delta ? ' ' + delta : ''}`);
      lines.push(fenHistory[i]);
      lines.push('');
    }

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Save'), 1500);
    }).catch(() => {});
  }

  // No-op handlers for Board (replay is non-interactive)
  const noopDrop = () => {};
  const noopDragOver = () => {};

  const displayName = gameRecord.gameName || `Game vs ${opponent}`;

  return (
    <div className={styles.container}>
      <GlobalHeader />

      {/* Back arrow - top left */}
      <button className={styles.homeBackButton} onClick={() => { playUISound(); onClose(); }} aria-label="Back to History">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      {/* Left Panel */}
      <div className={styles.leftPanel}>
        <h1 className={styles.panelTitle}>Game Replay</h1>
        <span className={styles.gameName}>{displayName}</span>

        {/* Game Info */}
        <div className={styles.section}>
          <span className={styles.sectionLabel}>Game Details</span>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoValue}>
                {gameRecord.result === 'draw'
                  ? 'Draw'
                  : gameRecord.result === 'light'
                    ? gameRecord.lightPlayerUsername
                    : gameRecord.darkPlayerUsername}
              </span>
              <span className={styles.infoLabel}>Winner</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoValue}>{(() => {
                const wc = gameRecord.winCondition;
                if (!wc) return 'N/A';
                const labels: Record<string, string> = {
                  'kingslayer': 'Kingslayer',
                  'race': 'Race',
                  'zombie-race': 'Zombie Race',
                  'timeout': 'Time-out',
                  'draw-stalemate': 'Draw Stalemate',
                  'draw-3-peat': 'Draw 3-peat',
                  'forfeit': 'Forfeit',
                };
                return labels[wc] || wc;
              })()}</span>
              <span className={styles.infoLabel}>Win Type</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoValue}>{gameRecord.turnCount}</span>
              <span className={styles.infoLabel}>Turns</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoValue}>{gameRecord.timeControlPreset.charAt(0).toUpperCase() + gameRecord.timeControlPreset.slice(1)}</span>
              <span className={styles.infoLabel}>Time Control</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoValue}>{(() => {
                const t = String(gameRecord.timestamp);
                const date = t.length === 8
                  ? new Date(`${t.slice(0,4)}-${t.slice(4,6)}-${t.slice(6,8)}`)
                  : new Date(gameRecord.timestamp);
                const day = date.getDate();
                const suffix = [11,12,13].includes(day) ? 'th' : ['st','nd','rd'][(day % 10) - 1] || 'th';
                const month = date.toLocaleString(undefined, { month: 'long' });
                return `${day}${suffix} of ${month} ${date.getFullYear()}`;
              })()}</span>
              <span className={styles.infoLabel}>Date</span>
            </div>
          </div>
        </div>

        {/* Take Over */}
        <div className={styles.section}>
          <span className={styles.sectionLabel}>Take Over From Here</span>
          <div className={styles.takeoverButtons}>
            <button
              className={styles.takeoverButton}
              onClick={() => handleTakeoverClick('light')}
            >
              Play as Light (vs AI)
            </button>
            <button
              className={styles.takeoverButton}
              onClick={() => handleTakeoverClick('dark')}
            >
              Play as Dark (vs AI)
            </button>
            <button
              className={`${styles.takeoverButton} ${styles.sandboxButton}`}
              onClick={handleSandbox}
            >
              Open in Sandbox
            </button>
          </div>
        </div>
      </div>

      {/* Game Area (Board + Right Panel) */}
      <div className={styles.gameArea}>
        <div className={styles.boardWrapper}>
          <div className={styles.darkPlayerLabel}>
            <span className={styles.playerLabelSide}>Dark</span>
            <span className={styles.playerLabelName}>{gameRecord.darkPlayerUsername}</span>
            {isArenaVariantId(gameRecord.darkPlayerId) && (
              <span className={styles.playerDescriptor}>{resolveVariantDescriptor(gameRecord.darkPlayerId)}</span>
            )}
          </div>
          <div className={styles.boardContainer}>
            {replayGameState && (
              <Board
                gameState={replayGameState}
                uiState={{ potentialMoves: [], potentialSideEffects: [] }}
                onDrop={noopDrop}
                onDragOver={noopDragOver}
              />
            )}
          </div>
          <div className={styles.lightPlayerLabel}>
            <span className={styles.playerLabelSide}>Light</span>
            <span className={styles.playerLabelName}>{gameRecord.lightPlayerUsername}</span>
            {isArenaVariantId(gameRecord.lightPlayerId) && (
              <span className={styles.playerDescriptor}>{resolveVariantDescriptor(gameRecord.lightPlayerId)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - fully independent from board */}
      <div className={styles.rightPanel}>
        <div className={styles.moveDelta}>
          {moveDescription || '\u2014'}
        </div>
        <div className={styles.boardControls}>
          <div className={styles.controls}>
            <button
              className={styles.controlButton}
              onClick={() => navigateTo(0)}
              disabled={currentIndex === 0}
              title="Start (Home)"
            >
              &#x23EE;
            </button>
            <button
              className={styles.controlButton}
              onClick={() => navigateTo(currentIndex - 1)}
              disabled={currentIndex === 0}
              title="Back (Left Arrow)"
            >
              &#x23F4;
            </button>
            <button
              className={styles.controlButton}
              onClick={() => navigateTo(currentIndex + 1)}
              disabled={currentIndex >= totalMoves - 1}
              title="Forward (Right Arrow)"
            >
              &#x23F5;
            </button>
            <button
              className={styles.controlButton}
              onClick={() => navigateTo(totalMoves - 1)}
              disabled={currentIndex >= totalMoves - 1}
              title="End (End)"
            >
              &#x23ED;
            </button>
          </div>
          <div className={styles.moveIndicator}>
            Move {currentIndex}/{totalMoves - 1}
          </div>
          <div className={styles.turnIndicator}>
            Current player to move: {replayGameState?.isLightTurn ? 'Dark' : 'Light'}
          </div>
        </div>
        <div className={styles.showKingsToggle}>
          <button
            className={`${styles.toggleButton} ${showKings ? styles.toggleButtonActive : ''}`}
            onClick={() => setShowKings((prev) => !prev)}
            title="Toggle king visibility"
          >
            {showKings ? 'Hide Kings' : 'Show Kings'}
          </button>
          <button
            className={styles.toggleButton}
            onClick={handleCopyGame}
            title="Copy all FENs and move deltas to clipboard"
          >
            {copyLabel}
          </button>
        </div>
      </div>

      {/* AI Selection Modal */}
      <AIModelSelectionModal
        isOpen={showAISelector}
        selectedModel={selectedModel}
        onSelect={handleAIModelSelect}
        onClose={handleCancelTakeover}
      />

      {/* Time frame selector (shown after AI model is selected) */}
      {selectedModel && takeoverSide && (
        <div className={styles.aiSelectorOverlay} onClick={handleCancelTakeover}>
          <div className={styles.aiSelectorPanel} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.aiSelectorTitle}>
              Select AI Speed
            </h3>
            <div className={styles.timeFrameSelector}>
              {[
                { frame: TimeFrame.FAST, label: 'Fast (2s)' },
                { frame: TimeFrame.STANDARD, label: 'Standard (7s)' },
                { frame: TimeFrame.DEEP, label: 'Deep (15s)' },
              ].map(({ frame, label }) => (
                <button
                  key={frame}
                  className={`${styles.timeFrameButton} ${selectedTimeFrame === frame ? styles.timeFrameButtonActive : ''}`}
                  onClick={() => setSelectedTimeFrame(frame)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className={styles.aiSelectorActions}>
              <button className={styles.cancelButton} onClick={handleCancelTakeover}>
                Cancel
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmTakeover}
              >
                Start Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
